/**
 * /accounting/gl/journal-entries — list + manual posting dialog.
 *
 * Append-only. Posted entries can be reversed (creates a balancing reverse entry),
 * never edited in place.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, Trash2, RotateCcw, Eye, EyeOff, Download } from "lucide-react";
import { exportCsv } from "@/lib/csvExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CockpitPage, CockpitSection, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAppData } from "@/data/AppDataContext";
import { resolveEvidence } from "@/data/accounting/evidenceResolver";
import { JournalEvidencePanel } from "@/components/accounting/JournalEvidencePanel";
import type { JournalEntry } from "@/data/accounting/glTypes";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface DraftLine {
  accountId: string;
  side: "debit" | "credit";
  amount: string;
  memo: string;
  customerId: string;
  vendorId: string;
  jobId: string;
}

const emptyLine = (): DraftLine => ({
  accountId: "", side: "debit", amount: "", memo: "",
  customerId: "", vendorId: "", jobId: "",
});

export default function JournalEntriesPage() {
  const { accounts, journalEntries, postJournal, reverseJournal } = useGeneralLedger();
  const [params] = useSearchParams();
  const focusId = params.get("focus") ?? undefined;
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  // When ?focus=<jeId> lands, seed the search with the JE number and scroll it
  // into view with a transient highlight. This is the drill-down landing spot
  // from GL Detail / Trial Balance.
  useEffect(() => {
    if (!focusId) return;
    const je = journalEntries.find(j => j.id === focusId);
    if (je) setQ(je.number);
    const el = document.getElementById(`je-${focusId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusId, journalEntries]);


  const filtered = useMemo(() => {
    if (!q.trim()) return journalEntries;
    const s = q.toLowerCase();
    return journalEntries.filter(j =>
      j.number.toLowerCase().includes(s) ||
      j.description.toLowerCase().includes(s) ||
      j.source.toLowerCase().includes(s));
  }, [journalEntries, q]);

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Journal Entries</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Append-only ledger. Posted entries reverse via balancing entry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64"><CockpitSearch value={q} onChange={setQ} placeholder="JE#, description, source…" /></div>
          <Button variant="outline" size="sm" onClick={() => {
            const acctById = Object.fromEntries(accounts.map(a => [a.id, a]));
            exportCsv(
              `journal-entries-${new Date().toISOString().slice(0, 10)}.csv`,
              ["JE#", "Date", "Period", "Status", "Source", "Description", "Account#", "Account", "Debit", "Credit", "Customer", "Vendor", "Job", "Trade", "Memo"],
              filtered.flatMap(je => je.lines.map(l => {
                const a = acctById[l.accountId];
                return [
                  je.number, je.postingDate, je.period, je.status, je.source, je.description,
                  a?.number ?? l.accountId, a?.name ?? "",
                  l.side === "debit" ? l.amount.toFixed(2) : "",
                  l.side === "credit" ? l.amount.toFixed(2) : "",
                  l.customerId ?? "", l.vendorId ?? "", l.jobId ?? "", l.tradeType ?? "", l.memo ?? "",
                ];
              })),
            );
          }} disabled={filtered.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1.5" /> New entry</Button>
            </DialogTrigger>
            <NewEntryDialog onClose={() => setOpen(false)} />
          </Dialog>
        </div>
      </div>

      <CockpitSection>
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="rounded-md border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              No journal entries yet. Post one to begin.
            </div>
          ) : filtered.map(je => (
            <EntryCard key={je.id} entry={je} focused={je.id === focusId} accountsById={Object.fromEntries(accounts.map(a => [a.id, a]))} onReverse={(reason) => {
              try { reverseJournal(je.id, reason); toast.success(`Reversed ${je.number}`); }
              catch (e: any) { toast.error(e.message); }
            }} />
          ))}
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function EntryCard({
  entry, accountsById, onReverse, focused,
}: {
  entry: JournalEntry;
  accountsById: Record<string, { number: string; name: string }>;
  onReverse: (reason: string) => void;
  focused?: boolean;
}) {
  const appData = useAppData();
  const { jobs, installerWrapRecords } = appData;
  const payRunRecords: any[] = (appData as any).payRunRecords ?? [];
  const [showEvidence, setShowEvidence] = useState(false);
  const dr = entry.lines.filter(l => l.side === "debit").reduce((s, l) => s + l.amount, 0);
  const cr = entry.lines.filter(l => l.side === "credit").reduce((s, l) => s + l.amount, 0);
  const evidence = useMemo(
    () => resolveEvidence(entry, { jobs, payRunRecords, installerWrapRecords }),
    [entry, jobs, payRunRecords, installerWrapRecords],
  );
  const hasEvidence = evidence.length > 0;
  return (
    <div id={`je-${entry.id}`} className={`rounded-md border bg-card overflow-hidden transition-shadow ${focused ? "border-primary ring-2 ring-primary/30 shadow-md" : "border-border"}`}>
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-mono text-xs text-muted-foreground">{entry.number}</span>
          <span className="text-sm font-medium truncate">{entry.description}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant="outline" className="text-[10px] capitalize">{entry.source.replace(/_/g, " ")}</Badge>
          {hasEvidence ? (
            <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
              {evidence.length} proof
            </Badge>
          ) : null}
          {entry.status === "void"
            ? <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Void</Badge>
            : <span className="text-xs text-muted-foreground">{entry.postingDate}</span>}
          {hasEvidence ? (
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setShowEvidence(s => !s)}>
              {showEvidence
                ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide evidence</>
                : <><Eye className="h-3.5 w-3.5 mr-1" /> Evidence</>}
            </Button>
          ) : null}
          {entry.status === "posted" ? (
            <Button variant="ghost" size="sm" className="h-7" onClick={() => {
              const reason = prompt(`Reverse ${entry.number}. Reason?`);
              if (reason && reason.trim().length >= 4) onReverse(reason.trim());
              else if (reason) alert("Reason must be ≥4 characters.");
            }}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reverse
            </Button>
          ) : null}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="px-4 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-left">Memo / Ref</th>
            <th className="px-3 py-2 text-right w-28">Debit</th>
            <th className="px-3 py-2 text-right w-28">Credit</th></tr>
        </thead>
        <tbody>
          {entry.lines.map(l => {
            const a = accountsById[l.accountId];
            return (
              <tr key={l.id} className="border-t border-border">
                <td className="px-4 py-1.5">
                  <span className="font-mono text-xs text-muted-foreground mr-2">{a?.number}</span>
                  {a?.name}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">
                  {l.memo && <span>{l.memo}</span>}
                  {l.customerId && <span> · cust:{l.customerId}</span>}
                  {l.vendorId && <span> · vend:{l.vendorId}</span>}
                  {l.jobId && (
                    <>
                      {" · "}
                      <Link to={`/jobs/${l.jobId}`} className="text-primary hover:underline">
                        WO#{l.jobId}
                      </Link>
                    </>
                  )}
                  {l.tradeType && <span> · {l.tradeType}</span>}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{l.side === "debit" ? fmt(l.amount) : ""}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{l.side === "credit" ? fmt(l.amount) : ""}</td>
              </tr>
            );
          })}
          <tr className="border-t border-border bg-muted/20 text-xs">
            <td colSpan={2} className="px-4 py-1.5 text-right text-muted-foreground">Totals</td>
            <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(dr)}</td>
            <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(cr)}</td>
          </tr>
        </tbody>
      </table>
      {showEvidence ? <JournalEvidencePanel items={evidence} /> : null}
    </div>
  );
}

function NewEntryDialog({ onClose }: { onClose: () => void }) {
  const { accounts, postJournal } = useGeneralLedger();
  const [postingDate, setPostingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);

  const updateLine = (i: number, patch: Partial<DraftLine>) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const dr = lines.reduce((s, l) => s + (l.side === "debit" ? Number(l.amount) || 0 : 0), 0);
  const cr = lines.reduce((s, l) => s + (l.side === "credit" ? Number(l.amount) || 0 : 0), 0);
  const balanced = Math.round(dr * 100) === Math.round(cr * 100) && dr > 0;

  const submit = () => {
    if (!description.trim()) { toast.error("Description required"); return; }
    if (!balanced) { toast.error("Entry is not balanced"); return; }
    try {
      const je = postJournal({
        postingDate,
        description: description.trim(),
        source: "manual",
        lines: lines
          .filter(l => l.accountId && Number(l.amount) > 0)
          .map(l => ({
            accountId: l.accountId,
            side: l.side,
            amount: Number(l.amount),
            memo: l.memo || undefined,
            customerId: l.customerId || undefined,
            vendorId: l.vendorId || undefined,
            jobId: l.jobId || undefined,
          })),
      });
      toast.success(`Posted ${je.number}`);
      onClose();
    } catch (e: any) { toast.error(e.message); }
  };

  const sortedAccounts = useMemo(
    () => [...accounts].filter(a => a.active).sort((a, b) => a.number.localeCompare(b.number)),
    [accounts],
  );

  // Keyboard shortcuts inside the dialog:
  //   Ctrl/⌘ + Enter → Post entry (if balanced)
  //   Ctrl/⌘ + L     → Add line
  //   Esc            → handled by Dialog itself (cancel)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key === "Enter") {
        e.preventDefault();
        if (balanced) submit();
      } else if (mod && (e.key === "l" || e.key === "L")) {
        e.preventDefault();
        addLine();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [balanced, submit]);

  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>New Journal Entry</DialogTitle></DialogHeader>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Posting date</Label>
          <Input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Period</Label>
          <Input value={postingDate.slice(0, 7)} disabled />
        </div>
      </div>

      <div>
        <Label className="text-xs">Description</Label>
        <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Petty cash reimbursement — June" />
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left w-[240px]">Account</th>
              <th className="px-2 py-1.5 text-left w-24">Side</th>
              <th className="px-2 py-1.5 text-right w-28">Amount</th>
              <th className="px-2 py-1.5 text-left">Memo / Subledger</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const acc = accounts.find(a => a.id === l.accountId);
              return (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5">
                    <Select value={l.accountId} onValueChange={v => updateLine(i, { accountId: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select account…" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {sortedAccounts.map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-xs">
                            <span className="font-mono mr-2">{a.number}</span> {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Select value={l.side} onValueChange={(v: "debit" | "credit") => updateLine(i, { side: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit">Debit</SelectItem>
                        <SelectItem value="credit">Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input className="h-8 text-xs text-right tabular-nums" inputMode="decimal"
                      value={l.amount} onChange={e => updateLine(i, { amount: e.target.value })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input className="h-8 text-xs" placeholder={
                      acc?.requiresSubledger === "customer" ? "Customer ID required + memo"
                      : acc?.requiresSubledger === "vendor" ? "Vendor ID required + memo"
                      : acc?.requiresSubledger === "job" ? "WO# required + memo"
                      : "Memo"
                    } value={l.memo} onChange={e => updateLine(i, { memo: e.target.value })} />
                    {acc?.requiresSubledger ? (
                      <Input className="h-7 text-xs mt-1" placeholder={`${acc.requiresSubledger} id`}
                        value={
                          acc.requiresSubledger === "customer" ? l.customerId
                          : acc.requiresSubledger === "vendor" ? l.vendorId
                          : l.jobId
                        }
                        onChange={e => updateLine(i,
                          acc.requiresSubledger === "customer" ? { customerId: e.target.value }
                          : acc.requiresSubledger === "vendor" ? { vendorId: e.target.value }
                          : { jobId: e.target.value },
                        )} />
                    ) : null}
                  </td>
                  <td className="px-1">
                    {lines.length > 2 ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-muted/20">
            <tr className="border-t border-border text-xs">
              <td className="px-2 py-1.5 text-right text-muted-foreground" colSpan={2}>Totals</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(dr)} DR</td>
              <td className="px-2 py-1.5 tabular-nums">{fmt(cr)} CR</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={addLine}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add line
        </Button>
        <div className={`text-xs ${balanced ? "text-foreground" : "text-destructive"}`}>
          {balanced ? "Balanced" : `Out of balance by ${fmt(Math.abs(dr - cr))}`}
        </div>
      </div>

      <DialogFooter className="sm:justify-between">
        <div className="text-[10px] text-muted-foreground">
          Shortcuts: <kbd className="px-1 py-0.5 border rounded">⌘L</kbd> add line ·{" "}
          <kbd className="px-1 py-0.5 border rounded">⌘↵</kbd> post
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!balanced}>Post entry</Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
}
